import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AnimationIcon from "@mui/icons-material/Animation";
import {
  BURST_PRESETS,
  DIRECTIONS,
  EDGE_PRESETS,
  GARLAND_PRESETS,
  GREETING_EFFECTS,
  HomeTheme,
  LAYER_MOTIONS,
  LIMITS,
  LayerType,
  MAX_LAYERS,
  PARTICLE_PRESETS,
  RANGES,
  ThemeColors,
  ThemeConfig,
  ThemeLayer,
  activateTheme,
  isColor,
  isHttpUrl,
  scheduleTheme,
  updateTheme,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/homeThemesService";
import { TabShape } from "../TabShapeEditor";
import { ThemePreview } from "./ThemePreview";
import { AssetUrlField, ChipsInput, ColorField, SliderRow, pct, times } from "./fields";
import {
  GREEN,
  GREEN_DARK,
  contrastIssues,
  deepEqual,
  effectiveColors,
  errMsg,
  isoToLocalInput,
  localInputToIso,
  statusChip,
} from "./themeUtils";

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const PARTICLE_LABELS: Record<string, string> = {
  none: "None",
  snow: "Snow",
  rain: "Rain",
  leaves: "Falling leaves",
  petals: "Petals",
  sparkles: "Sparkles",
  confetti: "Confetti",
  fireworks: "Fireworks",
  embers: "Embers",
  bubbles: "Bubbles",
  lanterns: "Sky lanterns",
  stars: "Twinkling stars",
};
const GARLAND_LABELS: Record<string, string> = {
  none: "None",
  toran: "Toran (triangle flags)",
  lights: "String lights",
  marigold: "Marigold",
  bells: "Bells",
  stars: "Hanging stars",
  mango: "Mango leaves",
};
const MOTION_LABELS: Record<string, string> = {
  none: "None",
  float: "Float",
  sway: "Sway",
  spin: "Spin",
  pulse: "Pulse",
  flyby: "Fly across",
};
const BURST_LABELS: Record<string, string> = {
  cracker: "Cracker",
  confetti: "Confetti",
  snow: "Snow",
  hearts: "Hearts",
  petals: "Petals",
};

const GLYPH_SUGGESTIONS = ["❄️", "🍁", "🍂", "🌸", "🌼", "🌾", "✨", "🎉", "⭐", "🪔", "🎈", "💧"];
const CITY_SUGGESTIONS = ["Madurai", "Chennai", "Coimbatore", "Tiruchirappalli", "Salem", "Tirunelveli"];

type ColorKey = Exclude<keyof ThemeColors, "dashDirection" | "statusBar">;

const COLOR_GROUPS: Array<{ title: string; fields: Array<{ key: ColorKey; label: string; optional?: boolean }> }> = [
  {
    title: "Dash",
    fields: [
      { key: "dashFrom", label: "Dash start" },
      { key: "dashVia", label: "Dash middle (optional)", optional: true },
      { key: "dashTo", label: "Dash end" },
      { key: "onDash", label: "Text & icons on the dash" },
    ],
  },
  {
    title: "Selected tab",
    fields: [
      { key: "tabSelectedFill", label: "Fill" },
      { key: "tabSelectedRing", label: "Ring" },
      { key: "tabSelectedText", label: "Text" },
      { key: "tabUnderlineSelected", label: "Underline" },
    ],
  },
  {
    title: "Other tabs",
    fields: [
      { key: "tabFrom", label: "Gradient start" },
      { key: "tabTo", label: "Gradient end" },
      { key: "tabText", label: "Text" },
      { key: "tabUnderline", label: "Underline" },
    ],
  },
  {
    title: "Search bar",
    fields: [
      { key: "searchBg", label: "Background" },
      { key: "searchText", label: "Placeholder text" },
      { key: "searchIcon", label: "Search icon" },
      { key: "micColor", label: "Mic icon" },
    ],
  },
  { title: "Accent", fields: [{ key: "accent", label: "Accent (buttons, highlights)" }] },
];

const TABS = [
  "Colours",
  "Night",
  "Background",
  "Layers",
  "Particles",
  "Garland & edge",
  "Greeting",
  "Interaction",
  "3D model",
  "Schedule & targeting",
] as const;

type Draft = {
  name: string;
  config: ThemeConfig;
  priority: number;
  cities: string[];
  starts_at: string | null;
  ends_at: string | null;
};

const toDraft = (t: HomeTheme): Draft => ({
  name: t.name,
  config: t.config,
  priority: t.priority ?? 0,
  cities: t.cities ?? [],
  starts_at: t.starts_at,
  ends_at: t.ends_at,
});

const Section: React.FC<{ title: string; children: React.ReactNode; hint?: string }> = ({ title, children, hint }) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={{ fontWeight: 700, fontSize: 14.5, mb: hint ? 0.25 : 1.25 }}>{title}</Typography>
    {hint && <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1.25 }}>{hint}</Typography>}
    {children}
  </Box>
);

const Grid2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>{children}</Box>
);

const Toggle: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }> = ({
  label,
  checked,
  onChange,
  hint,
}) => (
  <Box>
    <FormControlLabel
      control={
        <Switch
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          sx={{ "& .Mui-checked": { color: `${GREEN} !important` }, "& .Mui-checked + .MuiSwitch-track": { bgcolor: `${GREEN} !important` } }}
        />
      }
      label={<Typography sx={{ fontSize: 14 }}>{label}</Typography>}
    />
    {hint && <Typography sx={{ fontSize: 12, color: "text.secondary", mt: -0.5, ml: 6 }}>{hint}</Typography>}
  </Box>
);

/* ------------------------------- layers ------------------------------- */

const newLayer = (type: LayerType): ThemeLayer => ({
  id: `layer-${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`,
  type,
  src: type === "emoji" ? "✨" : "",
  x: 0.5,
  y: 0.5,
  w: type === "emoji" ? 0.1 : 0.3,
  opacity: 1,
  loop: true,
  speed: 1,
  depth: 0.5,
  every: 0,
  motion: type === "emoji" ? "float" : "none",
  front: false,
});

const layerProblem = (l: ThemeLayer): string | null => {
  if (l.type === "emoji") return l.src.trim() ? null : "Add an emoji, or this layer is dropped on save";
  return isHttpUrl(l.src) ? null : "Add a file link, or this layer is dropped on save";
};

const LayerIcon: React.FC<{ type: LayerType }> = ({ type }) =>
  type === "emoji" ? (
    <EmojiEmotionsOutlinedIcon fontSize="small" />
  ) : type === "image" ? (
    <ImageOutlinedIcon fontSize="small" />
  ) : (
    <AnimationIcon fontSize="small" />
  );

const LayersPanel: React.FC<{
  layers: ThemeLayer[];
  onChange: (l: ThemeLayer[]) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  themeId: number;
}> = ({ layers, onChange, selectedId, onSelect, themeId }) => {
  const set = (i: number, patch: Partial<ThemeLayer>) =>
    onChange(layers.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= layers.length) return;
    const next = [...layers];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const full = layers.length >= MAX_LAYERS;
  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5 }}>
        Emoji, pictures and Lottie animations placed on the dash. Drag a layer on the preview to move it.
        Later layers draw on top. ({layers.length}/{MAX_LAYERS})
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {(["emoji", "image", "lottie"] as LayerType[]).map((t) => (
          <Button
            key={t}
            variant="outlined"
            startIcon={<LayerIcon type={t} />}
            disabled={full}
            onClick={() => {
              const l = newLayer(t);
              onChange([...layers, l]);
              onSelect(l.id);
            }}
            sx={{ textTransform: "none", borderColor: GREEN, color: GREEN_DARK }}
          >
            Add {t === "emoji" ? "emoji" : t === "image" ? "image" : "Lottie"}
          </Button>
        ))}
      </Stack>
      {full && (
        <Alert severity="info" sx={{ mb: 2 }}>
          A theme can have up to {MAX_LAYERS} layers. Delete one to add another.
        </Alert>
      )}
      {layers.length === 0 && (
        <Typography sx={{ fontSize: 13, color: "text.secondary", py: 2 }}>No layers yet.</Typography>
      )}
      <Stack spacing={1}>
        {layers.map((l, i) => {
          const open = selectedId === l.id;
          const problem = layerProblem(l);
          return (
            <Box
              key={l.id}
              sx={{
                border: `1.5px solid ${open ? GREEN : "#E3E8E5"}`,
                borderRadius: 2,
                bgcolor: open ? "#F6FBF8" : "#fff",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 1.25, py: 0.75, cursor: "pointer" }}
                onClick={() => onSelect(open ? null : l.id)}
              >
                <LayerIcon type={l.type} />
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {l.type === "emoji" ? `${l.src || "Emoji"}` : l.src ? l.src.split("/").pop() : `${cap(l.type)} (no file yet)`}
                  <Typography component="span" sx={{ fontSize: 12, color: "text.secondary", ml: 1 }}>
                    {MOTION_LABELS[l.motion]}
                    {l.front ? " · in front" : ""}
                  </Typography>
                </Typography>
                {problem && <Chip size="small" color="warning" label="Needs a source" />}
                <Tooltip title="Move up">
                  <span>
                    <IconButton size="small" disabled={i === 0} onClick={(e) => { e.stopPropagation(); move(i, -1); }} aria-label="Move layer up">
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton size="small" disabled={i === layers.length - 1} onClick={(e) => { e.stopPropagation(); move(i, 1); }} aria-label="Move layer down">
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete layer">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(layers.filter((_, j) => j !== i));
                      if (open) onSelect(null);
                    }}
                    aria-label="Delete layer"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              {open && (
                <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
                  {l.type === "emoji" ? (
                    <TextField
                      size="small"
                      label="Emoji"
                      value={l.src}
                      onChange={(e) => set(i, { src: e.target.value.slice(0, LIMITS.emoji) })}
                      helperText={problem ?? "One or a few emoji, up to 16 characters"}
                      error={!!problem}
                      fullWidth
                      sx={{ mb: 1.5 }}
                    />
                  ) : (
                    <Box sx={{ mb: 1.5 }}>
                      <AssetUrlField
                        label={l.type === "image" ? "Image link" : "Lottie link (.json)"}
                        kind={l.type === "image" ? "image" : "lottie"}
                        value={l.src || null}
                        onChange={(v) => set(i, { src: v ?? "" })}
                        themeId={themeId}
                        hint={l.type === "lottie" ? "The preview shows a placeholder; the app plays the animation." : undefined}
                      />
                    </Box>
                  )}
                  <Grid2>
                    <SliderRow label="Across (x)" value={l.x} {...RANGES.layerX} onChange={(v) => set(i, { x: v })} format={pct} />
                    <SliderRow label="Down (y)" value={l.y} {...RANGES.layerY} onChange={(v) => set(i, { y: v })} format={pct} />
                    <SliderRow label="Width" value={l.w} {...RANGES.layerW} onChange={(v) => set(i, { w: v })} format={pct} hint="Share of the dash width" />
                    <SliderRow label="Opacity" value={l.opacity} {...RANGES.layerOpacity} onChange={(v) => set(i, { opacity: v })} format={pct} />
                    <SliderRow label="Depth" value={l.depth} {...RANGES.layerDepth} onChange={(v) => set(i, { depth: v })} format={(v) => (v < 0.34 ? "Far" : v > 0.66 ? "Near" : "Middle")} hint="Near layers move more when the phone tilts" />
                    <SliderRow label="Speed" value={l.speed} {...RANGES.layerSpeed} onChange={(v) => set(i, { speed: v })} format={times} />
                  </Grid2>
                  <Grid2>
                    <TextField
                      select
                      size="small"
                      label="Motion"
                      value={l.motion}
                      onChange={(e) => set(i, { motion: e.target.value as ThemeLayer["motion"] })}
                      sx={{ mt: 1 }}
                    >
                      {LAYER_MOTIONS.map((m) => (
                        <MenuItem key={m} value={m}>
                          {MOTION_LABELS[m]}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      type="number"
                      label="Repeat every (seconds)"
                      value={l.every}
                      inputProps={{ min: RANGES.layerEvery.min, max: RANGES.layerEvery.max, step: 1 }}
                      onChange={(e) => {
                        const n = Math.round(Number(e.target.value) || 0);
                        set(i, { every: Math.max(RANGES.layerEvery.min, Math.min(RANGES.layerEvery.max, n)) });
                      }}
                      helperText="0 = continuous. Use with Fly across for an occasional pass."
                      sx={{ mt: 1 }}
                    />
                  </Grid2>
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    <Toggle label="In front of the tabs" checked={l.front} onChange={(v) => set(i, { front: v })} />
                    {l.type === "lottie" && <Toggle label="Loop" checked={l.loop} onChange={(v) => set(i, { loop: v })} />}
                  </Stack>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

/* -------------------------------- editor -------------------------------- */

export const ThemeEditor: React.FC<{
  theme: HomeTheme;
  tabShape: TabShape | null;
  onClose: () => void;
  /** Called after any successful save; the list should reload. */
  onSaved: (msg: string) => void;
}> = ({ theme, tabShape, onClose, onSaved }) => {
  const initial = React.useMemo(() => toDraft(theme), [theme]);
  const [draft, setDraft] = React.useState<Draft>(initial);
  const [tab, setTab] = React.useState(0);
  const [night, setNight] = React.useState(false);
  const [lang, setLang] = React.useState<"en" | "ta">("en");
  const [selectedLayer, setSelectedLayer] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState<null | "save" | "activate" | "schedule">(null);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);

  const dirty = !deepEqual(draft, initial);
  const cfg = draft.config;
  const isDefault = theme.is_default;

  React.useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const setCfg = (fn: (c: ThemeConfig) => ThemeConfig) => setDraft((d) => ({ ...d, config: fn(d.config) }));
  const patch = <K extends keyof ThemeConfig>(k: K, v: Partial<ThemeConfig[K]>) =>
    setCfg((c) => ({ ...c, [k]: { ...(c[k] as object), ...(v as object) } }));
  const setColor = (k: keyof ThemeColors, v: any) => setCfg((c) => ({ ...c, colors: { ...c.colors, [k]: v } }));
  const setNightColor = (k: keyof ThemeColors, v: any) =>
    setCfg((c) => {
      const n: Partial<ThemeColors> = { ...(c.night ?? {}) };
      if (v === undefined) delete n[k];
      else (n as any)[k] = v;
      return { ...c, night: Object.keys(n).length ? n : null };
    });

  const requestClose = () => {
    if (dirty && !saving) setConfirmDiscard(true);
    else if (!saving) onClose();
  };

  const brokenLayers = cfg.layers.filter((l) => layerProblem(l)).length;
  const windowBad =
    !!draft.starts_at && !!draft.ends_at && new Date(draft.ends_at).getTime() <= new Date(draft.starts_at).getTime();
  const canSchedule = !isDefault && !!draft.starts_at && !!draft.ends_at && !windowBad;

  const save = async (then?: "activate" | "schedule") => {
    if (!draft.name.trim()) {
      setError("Give the theme a name.");
      return;
    }
    if (windowBad) {
      setError("The end must be after the start.");
      setTab(TABS.indexOf("Schedule & targeting"));
      return;
    }
    setSaving(then ?? "save");
    setError(null);
    try {
      await updateTheme(theme.id, {
        name: draft.name.trim(),
        config: cfg,
        ...(isDefault
          ? {}
          : {
              priority: draft.priority,
              cities: draft.cities,
              starts_at: draft.starts_at,
              ends_at: draft.ends_at,
            }),
      });
      if (then === "activate") {
        await activateTheme(theme.id);
        onSaved(isDefault ? "Saved. The app is back on the default theme." : `Saved. “${draft.name.trim()}” is live now.`);
      } else if (then === "schedule") {
        await scheduleTheme(theme.id, {
          starts_at: draft.starts_at as string,
          ends_at: draft.ends_at as string,
          priority: draft.priority,
        });
        onSaved(`Saved and scheduled “${draft.name.trim()}”.`);
      } else {
        onSaved("Saved. Open apps update within seconds.");
      }
      onClose();
    } catch (e) {
      setError(errMsg(e, "Save failed. Try again."));
    } finally {
      setSaving(null);
    }
  };

  const colors = effectiveColors(cfg, night);
  const contrast = contrastIssues(colors);
  const chip = statusChip(theme);

  /* ---------------------------- panels ---------------------------- */

  const colorsPanel = (
    <Box>
      <Grid2>
        <TextField
          select
          size="small"
          label="Dash direction"
          value={cfg.colors.dashDirection}
          onChange={(e) => setColor("dashDirection", e.target.value)}
        >
          {DIRECTIONS.map((d) => (
            <MenuItem key={d} value={d}>
              {cap(d)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Phone status bar"
          value={cfg.colors.statusBar}
          onChange={(e) => setColor("statusBar", e.target.value)}
          helperText="Light icons for dark dashes, dark icons for light ones"
        >
          <MenuItem value="light">Light icons</MenuItem>
          <MenuItem value="dark">Dark icons</MenuItem>
        </TextField>
      </Grid2>
      {COLOR_GROUPS.map((g) => (
        <Box key={g.title} sx={{ mt: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.25 }}>{g.title}</Typography>
          <Grid2>
            {g.fields.map((f) => (
              <ColorField
                key={f.key}
                label={f.label}
                value={cfg.colors[f.key] as string | null}
                onChange={(v) => setColor(f.key, v)}
                onClear={f.optional ? () => setColor(f.key, null) : undefined}
                placeholder={f.optional ? "None" : undefined}
              />
            ))}
          </Grid2>
        </Box>
      ))}
    </Box>
  );

  const nightOn = !!cfg.night;
  const nightPanel = (
    <Box>
      <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
        Optional colours used from 7 pm to 6 am on the phone. Leave a field empty to keep the day colour.
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Toggle
          label="Use night colours"
          checked={nightOn}
          onChange={(v) => {
            setCfg((c) => ({
              ...c,
              night: v ? { dashFrom: c.colors.dashFrom, dashTo: c.colors.dashTo } : null,
            }));
            setNight(v);
          }}
        />
        <Toggle label="Preview night" checked={night} onChange={setNight} />
      </Stack>
      {nightOn && (
        <>
          <TextField
            select
            size="small"
            label="Phone status bar at night"
            value={cfg.night?.statusBar ?? ""}
            onChange={(e) => setNightColor("statusBar", e.target.value || undefined)}
            sx={{ mb: 1, minWidth: 260 }}
          >
            <MenuItem value="">Same as day</MenuItem>
            <MenuItem value="light">Light icons</MenuItem>
            <MenuItem value="dark">Dark icons</MenuItem>
          </TextField>
          {COLOR_GROUPS.map((g) => (
            <Box key={g.title} sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.25 }}>{g.title}</Typography>
              <Grid2>
                {g.fields.map((f) => (
                  <ColorField
                    key={f.key}
                    label={f.label}
                    value={(cfg.night?.[f.key] as string | undefined) ?? ""}
                    onChange={(v) => setNightColor(f.key, v)}
                    onClear={() => setNightColor(f.key, undefined)}
                    placeholder={`Day: ${cfg.colors[f.key] ?? "none"}`}
                  />
                ))}
              </Grid2>
            </Box>
          ))}
        </>
      )}
    </Box>
  );

  const backgroundPanel = (
    <Box>
      <Section title="Background picture" hint="Drawn over the dash colours. Pick from the library or upload a PNG, JPG, WebP or GIF (up to 3 MB).">
        <AssetUrlField
          label="Image link"
          kind="image"
          value={cfg.background.imageUrl}
          onChange={(v) => patch("background", { imageUrl: v })}
          themeId={theme.id}
        />
      </Section>
      <Grid2>
        <SliderRow label="Opacity" value={cfg.background.opacity} {...RANGES.bgOpacity} onChange={(v) => patch("background", { opacity: v })} format={pct} />
        <SliderRow
          label="Parallax"
          value={cfg.background.parallax}
          {...RANGES.bgParallax}
          onChange={(v) => patch("background", { parallax: v })}
          format={pct}
          hint="How far it shifts when the phone tilts"
        />
      </Grid2>
      <Toggle
        label="Repeat as a pattern"
        checked={cfg.background.repeat}
        onChange={(v) => patch("background", { repeat: v })}
        hint="Off = the picture covers the dash once"
      />
    </Box>
  );

  const particlesPanel = (
    <Box>
      <TextField
        select
        size="small"
        label="Effect"
        value={cfg.particles.preset}
        onChange={(e) => patch("particles", { preset: e.target.value as ThemeConfig["particles"]["preset"] })}
        sx={{ minWidth: 260, mb: 2 }}
      >
        {PARTICLE_PRESETS.map((p) => (
          <MenuItem key={p} value={p}>
            {PARTICLE_LABELS[p]}
          </MenuItem>
        ))}
      </TextField>
      {cfg.particles.preset !== "none" && (
        <>
          <Grid2>
            <SliderRow label="Density" value={cfg.particles.density} {...RANGES.particleDensity} onChange={(v) => patch("particles", { density: v })} format={pct} />
            <SliderRow label="Speed" value={cfg.particles.speed} {...RANGES.particleSpeed} onChange={(v) => patch("particles", { speed: v })} format={times} />
            <SliderRow label="Size" value={cfg.particles.size} {...RANGES.particleSize} onChange={(v) => patch("particles", { size: v })} format={times} />
            <SliderRow
              label="Wind"
              value={cfg.particles.wind}
              {...RANGES.particleWind}
              onChange={(v) => patch("particles", { wind: v })}
              format={(v) => (Math.abs(v) < 0.03 ? "Still" : v < 0 ? `${Math.round(-v * 100)}% left` : `${Math.round(v * 100)}% right`)}
            />
          </Grid2>
          <Box sx={{ mt: 2 }}>
            <ChipsInput
              label="Colours"
              values={cfg.particles.colors}
              onChange={(v) => patch("particles", { colors: v })}
              max={LIMITS.particleColors}
              colors
              placeholder="#FFD166"
              validate={(v) => (isColor(v) ? null : "Not a colour")}
            />
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>Empty = the effect's own colours.</Typography>
          </Box>
          <Box sx={{ mt: 2 }}>
            <ChipsInput
              label="Emoji instead of shapes"
              values={cfg.particles.glyphs}
              onChange={(v) => patch("particles", { glyphs: v })}
              max={LIMITS.particleGlyphs}
              maxLength={LIMITS.glyph}
              placeholder="e.g. 🍁"
            />
            <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5, mt: 1 }}>
              {GLYPH_SUGGESTIONS.map((g) => (
                <Chip
                  key={g}
                  label={g}
                  size="small"
                  variant="outlined"
                  disabled={cfg.particles.glyphs.length >= LIMITS.particleGlyphs || cfg.particles.glyphs.includes(g)}
                  onClick={() => patch("particles", { glyphs: [...cfg.particles.glyphs, g] })}
                />
              ))}
            </Stack>
            {(cfg.particles.preset === "rain" || cfg.particles.preset === "fireworks") && cfg.particles.glyphs.length > 0 && (
              <Typography sx={{ fontSize: 12, color: "warning.main", mt: 0.5 }}>
                Rain and fireworks always draw their own shapes in this preview.
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  const garlandPanel = (
    <Box>
      <Section title="Garland" hint="Hangs along the top of the dash.">
        <TextField
          select
          size="small"
          label="Garland"
          value={cfg.garland.preset}
          onChange={(e) => patch("garland", { preset: e.target.value as ThemeConfig["garland"]["preset"] })}
          sx={{ minWidth: 260, mb: 2 }}
        >
          {GARLAND_PRESETS.map((g) => (
            <MenuItem key={g} value={g}>
              {GARLAND_LABELS[g]}
            </MenuItem>
          ))}
        </TextField>
        {cfg.garland.preset !== "none" && (
          <>
            <ChipsInput
              label="Colours"
              values={cfg.garland.colors}
              onChange={(v) => patch("garland", { colors: v })}
              max={LIMITS.garlandColors}
              colors
              placeholder="#FF8C00"
              validate={(v) => (isColor(v) ? null : "Not a colour")}
            />
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>Empty = the garland's own colours.</Typography>
            <Toggle label="Sway gently" checked={cfg.garland.sway} onChange={(v) => patch("garland", { sway: v })} />
          </>
        )}
      </Section>
      <Divider sx={{ mb: 2.5 }} />
      <Section title="Bottom edge" hint="The shape where the dash meets the page.">
        <Grid2>
          <TextField
            select
            size="small"
            label="Edge"
            value={cfg.edge.preset}
            onChange={(e) => patch("edge", { preset: e.target.value as ThemeConfig["edge"]["preset"] })}
          >
            {EDGE_PRESETS.map((p) => (
              <MenuItem key={p} value={p}>
                {cap(p)}
              </MenuItem>
            ))}
          </TextField>
          {cfg.edge.preset !== "flat" && (
            <SliderRow label="Size" value={cfg.edge.size} {...RANGES.edgeSize} onChange={(v) => patch("edge", { size: v })} format={times} />
          )}
        </Grid2>
      </Section>
    </Box>
  );

  const greetingPanel = (
    <Box>
      <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
        A short line under the address. Leave both empty to hide it.
      </Typography>
      <Stack spacing={2}>
        <TextField
          size="small"
          label="Greeting (English)"
          value={cfg.greeting.text}
          onChange={(e) => patch("greeting", { text: e.target.value.slice(0, LIMITS.greeting) })}
          helperText={`${cfg.greeting.text.length}/${LIMITS.greeting}`}
          fullWidth
        />
        <TextField
          size="small"
          label="Greeting (Tamil)"
          value={cfg.greeting.textTa}
          onChange={(e) => patch("greeting", { textTa: e.target.value.slice(0, LIMITS.greeting) })}
          helperText={`${cfg.greeting.textTa.length}/${LIMITS.greeting} · shown to users who use the app in Tamil`}
          fullWidth
        />
        <Grid2>
          <ColorField label="Text colour" value={cfg.greeting.color} onChange={(v) => patch("greeting", { color: v })} />
          <TextField
            select
            size="small"
            label="Effect"
            value={cfg.greeting.effect}
            onChange={(e) => patch("greeting", { effect: e.target.value as ThemeConfig["greeting"]["effect"] })}
          >
            {GREETING_EFFECTS.map((g) => (
              <MenuItem key={g} value={g}>
                {cap(g)}
              </MenuItem>
            ))}
          </TextField>
        </Grid2>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            type="date"
            label="Countdown to"
            InputLabelProps={{ shrink: true }}
            value={cfg.greeting.countdownTo ?? ""}
            onChange={(e) => patch("greeting", { countdownTo: e.target.value || null })}
            helperText="Before this date the app adds “in N days”; on and after it, just the greeting."
            sx={{ flex: 1 }}
          />
          {cfg.greeting.countdownTo && (
            <IconButton onClick={() => patch("greeting", { countdownTo: null })} aria-label="Clear countdown date">
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </Box>
  );

  const interactionPanel = (
    <Box>
      <Toggle
        label="Burst when the dash is tapped"
        checked={cfg.interaction.tapBurst}
        onChange={(v) => patch("interaction", { tapBurst: v })}
        hint="Click the preview to try it."
      />
      {cfg.interaction.tapBurst && (
        <Box sx={{ pl: 6, mt: 1, mb: 1 }}>
          <TextField
            select
            size="small"
            label="Burst"
            value={cfg.interaction.burst}
            onChange={(e) => patch("interaction", { burst: e.target.value as ThemeConfig["interaction"]["burst"] })}
            sx={{ minWidth: 220 }}
          >
            {BURST_PRESETS.map((b) => (
              <MenuItem key={b} value={b}>
                {BURST_LABELS[b]}
              </MenuItem>
            ))}
          </TextField>
          <Toggle label="Vibrate on tap" checked={cfg.interaction.haptic} onChange={(v) => patch("interaction", { haptic: v })} />
          <Box sx={{ mt: 1.5 }}>
            <AssetUrlField
              label="Tap sound (optional)"
              kind="audio"
              value={cfg.interaction.soundUrl}
              onChange={(v) => patch("interaction", { soundUrl: v })}
              themeId={theme.id}
              hint="A short MP3, M4A, AAC, WAV or OGG, up to 2 MB"
            />
            {isHttpUrl(cfg.interaction.soundUrl) && (
              <audio src={cfg.interaction.soundUrl as string} controls style={{ marginTop: 8, width: "100%" }} />
            )}
          </Box>
        </Box>
      )}
      <Divider sx={{ my: 2 }} />
      <Toggle
        label="Tilt parallax"
        checked={cfg.interaction.tilt}
        onChange={(v) => patch("interaction", { tilt: v })}
        hint="Layers and background shift with the phone's tilt. Move the mouse over the preview to try it."
      />
    </Box>
  );

  const modelPanel = (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Shows a real 3D model in the app. The preview marks where it sits and how big it is.
      </Alert>
      <AssetUrlField
        label="3D model link (.glb)"
        kind="glb"
        value={cfg.model3d.url}
        onChange={(v) => patch("model3d", { url: v })}
        themeId={theme.id}
        hint="GLB up to 8 MB. Keep it small so it loads fast on mobile data."
      />
      {cfg.model3d.url && (
        <Box sx={{ mt: 2 }}>
          <Grid2>
            <SliderRow label="Across (x)" value={cfg.model3d.x} {...RANGES.modelX} onChange={(v) => patch("model3d", { x: v })} format={pct} />
            <SliderRow label="Down (y)" value={cfg.model3d.y} {...RANGES.modelY} onChange={(v) => patch("model3d", { y: v })} format={pct} />
            <SliderRow label="Size" value={cfg.model3d.size} {...RANGES.modelSize} onChange={(v) => patch("model3d", { size: v })} format={pct} hint="Share of the dash width" />
            {cfg.model3d.autoRotate && (
              <SliderRow label="Rotate speed" value={cfg.model3d.rotateSpeed} {...RANGES.modelRotateSpeed} onChange={(v) => patch("model3d", { rotateSpeed: v })} format={times} />
            )}
          </Grid2>
          <Toggle label="Rotate by itself" checked={cfg.model3d.autoRotate} onChange={(v) => patch("model3d", { autoRotate: v })} />
        </Box>
      )}
    </Box>
  );

  const schedulePanel = isDefault ? (
    <Alert severity="info">
      This is the default theme. It shows whenever no other theme is live or scheduled, everywhere, so it has no
      schedule, priority or cities.
    </Alert>
  ) : (
    <Box>
      <Section
        title="When"
        hint="Saving stores these times. To run the theme automatically in this window, use “Save & schedule”. “Save & activate” turns it on now with no end and clears the times."
      >
        <Grid2>
          <Stack direction="row" spacing={0.5} alignItems="flex-start">
            <TextField
              size="small"
              type="datetime-local"
              label="Starts"
              InputLabelProps={{ shrink: true }}
              value={isoToLocalInput(draft.starts_at)}
              onChange={(e) => setDraft((d) => ({ ...d, starts_at: localInputToIso(e.target.value) }))}
              fullWidth
            />
            {draft.starts_at && (
              <IconButton onClick={() => setDraft((d) => ({ ...d, starts_at: null }))} aria-label="Clear start">
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="flex-start">
            <TextField
              size="small"
              type="datetime-local"
              label="Ends"
              InputLabelProps={{ shrink: true }}
              value={isoToLocalInput(draft.ends_at)}
              onChange={(e) => setDraft((d) => ({ ...d, ends_at: localInputToIso(e.target.value) }))}
              error={windowBad}
              helperText={windowBad ? "The end must be after the start" : undefined}
              fullWidth
            />
            {draft.ends_at && (
              <IconButton onClick={() => setDraft((d) => ({ ...d, ends_at: null }))} aria-label="Clear end">
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Grid2>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.75 }}>
          Times are in your computer's time zone ({Intl.DateTimeFormat().resolvedOptions().timeZone}).
        </Typography>
      </Section>
      <Section
        title="Priority"
        hint="When several themes are on at once: city-specific themes win, then the higher priority, then the most recently edited."
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <SliderRow
              label="Priority"
              value={draft.priority}
              {...RANGES.priority}
              onChange={(v) => setDraft((d) => ({ ...d, priority: v }))}
            />
          </Box>
          <TextField
            size="small"
            type="number"
            value={draft.priority}
            inputProps={{ min: -100, max: 100, step: 1, "aria-label": "Priority value" }}
            onChange={(e) => {
              const n = Math.round(Number(e.target.value) || 0);
              setDraft((d) => ({ ...d, priority: Math.max(-100, Math.min(100, n)) }));
            }}
            sx={{ width: 96 }}
          />
        </Stack>
      </Section>
      <Section title="Cities" hint="Only users in these cities see the theme. Leave empty for everywhere.">
        <ChipsInput
          label="Cities"
          values={draft.cities}
          onChange={(v) => setDraft((d) => ({ ...d, cities: v }))}
          max={LIMITS.cities}
          maxLength={60}
          placeholder="e.g. Madurai"
          validate={(v) =>
            draft.cities.some((c) => c.toLowerCase() === v.toLowerCase()) ? "Already added" : null
          }
        />
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5, mt: 1 }}>
          {CITY_SUGGESTIONS.filter((c) => !draft.cities.some((x) => x.toLowerCase() === c.toLowerCase())).map((c) => (
            <Chip
              key={c}
              label={`+ ${c}`}
              size="small"
              variant="outlined"
              disabled={draft.cities.length >= LIMITS.cities}
              onClick={() => setDraft((d) => ({ ...d, cities: [...d.cities, c] }))}
            />
          ))}
        </Stack>
      </Section>
    </Box>
  );

  const panels: Record<(typeof TABS)[number], React.ReactNode> = {
    Colours: colorsPanel,
    Night: nightPanel,
    Background: backgroundPanel,
    Layers: (
      <LayersPanel
        layers={cfg.layers}
        onChange={(layers) => setCfg((c) => ({ ...c, layers }))}
        selectedId={selectedLayer}
        onSelect={setSelectedLayer}
        themeId={theme.id}
      />
    ),
    Particles: particlesPanel,
    "Garland & edge": garlandPanel,
    Greeting: greetingPanel,
    Interaction: interactionPanel,
    "3D model": modelPanel,
    "Schedule & targeting": schedulePanel,
  };

  const busy = !!saving;

  return (
    <Dialog open fullScreen onClose={requestClose}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ md: "center" }}
        spacing={1.5}
        sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderBottom: "1px solid #E3E8E5", bgcolor: "#fff" }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          <IconButton onClick={requestClose} aria-label="Close editor">
            <CloseIcon />
          </IconButton>
          <TextField
            size="small"
            label="Theme name"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value.slice(0, LIMITS.name) }))}
            error={!draft.name.trim()}
            sx={{ width: { xs: "100%", sm: 320 } }}
          />
          <Chip size="small" label={chip.label} sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 600, display: { xs: "none", sm: "inline-flex" } }} />
          {isDefault && <Chip size="small" label="Default" variant="outlined" />}
          {dirty && (
            <Typography sx={{ fontSize: 12.5, color: "warning.main", whiteSpace: "nowrap" }}>Unsaved changes</Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
          <Button onClick={requestClose} disabled={busy} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          {canSchedule && (
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => save("schedule")}
              sx={{ textTransform: "none", borderColor: GREEN, color: GREEN_DARK }}
            >
              {saving === "schedule" ? "Scheduling…" : "Save & schedule"}
            </Button>
          )}
          <Button
            variant="outlined"
            disabled={busy}
            onClick={() => save("activate")}
            sx={{ textTransform: "none", borderColor: GREEN, color: GREEN_DARK }}
          >
            {saving === "activate" ? "Activating…" : isDefault ? "Save & use default" : "Save & activate"}
          </Button>
          <Button
            variant="contained"
            disabled={busy || !dirty}
            onClick={() => save()}
            startIcon={saving === "save" ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ bgcolor: GREEN, "&:hover": { bgcolor: GREEN_DARK }, textTransform: "none" }}
          >
            {saving === "save" ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, flex: 1, minHeight: 0, bgcolor: "#F6F8F7" }}>
        {/* preview */}
        <Box
          sx={{
            width: { md: 440 },
            flexShrink: 0,
            p: { xs: 2, md: 3 },
            overflowY: { md: "auto" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRight: { md: "1px solid #E3E8E5" },
          }}
        >
          <ThemePreview
            config={cfg}
            phone
            width={360}
            night={night}
            lang={lang}
            tabShape={tabShape}
            selectedLayerId={selectedLayer}
            onLayerSelect={(id) => {
              setSelectedLayer(id);
              if (id) setTab(TABS.indexOf("Layers"));
            }}
            onLayerMove={(id, x, y) =>
              setCfg((c) => ({ ...c, layers: c.layers.map((l) => (l.id === id ? { ...l, x, y } : l)) }))
            }
          />
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap justifyContent="center">
            <Chip
              label={night ? "Night preview" : "Day preview"}
              onClick={() => setNight((v) => !v)}
              variant={night ? "filled" : "outlined"}
              sx={night ? { bgcolor: "#1E2A3A", color: "#fff" } : undefined}
            />
            <Chip
              label={lang === "ta" ? "Tamil greeting" : "English greeting"}
              onClick={() => setLang((v) => (v === "en" ? "ta" : "en"))}
              variant={lang === "ta" ? "filled" : "outlined"}
            />
          </Stack>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1.5, textAlign: "center", maxWidth: 360 }}>
            A close match of the app, not exact. Drag layers to move them.
            {cfg.interaction.tapBurst ? " Click the dash to try the tap burst." : ""}
          </Typography>
          {night && !cfg.night && (
            <Alert severity="info" sx={{ mt: 1.5, maxWidth: 380 }}>
              No night colours set, so night looks the same as day.
            </Alert>
          )}
          {contrast.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5, maxWidth: 380 }}>
              Hard to read ({night ? "night" : "day"}), below 3:1:
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {contrast.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </Box>
            </Alert>
          )}
          {brokenLayers > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5, maxWidth: 380 }}>
              {brokenLayers === 1 ? "1 layer has" : `${brokenLayers} layers have`} no source and will be dropped on save.
            </Alert>
          )}
        </Box>

        {/* controls */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", bgcolor: "#fff" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: "1px solid #E3E8E5",
              px: 1,
              "& .MuiTab-root": { textTransform: "none", minWidth: 0, px: 1.75 },
              "& .Mui-selected": { color: `${GREEN_DARK} !important`, fontWeight: 700 },
              "& .MuiTabs-indicator": { bgcolor: GREEN },
            }}
          >
            {TABS.map((t) => (
              <Tab key={t} label={t === "Layers" && cfg.layers.length ? `Layers (${cfg.layers.length})` : t} />
            ))}
          </Tabs>
          <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 2, md: 3 } }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Box sx={{ maxWidth: 760 }}>{panels[TABS[tab]]}</Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)}>
        <DialogTitle>Discard changes?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>Your edits to “{theme.name}” have not been saved.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscard(false)} sx={{ textTransform: "none" }}>
            Keep editing
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={{ textTransform: "none" }}
            onClick={() => {
              setConfirmDiscard(false);
              onClose();
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ThemeEditor;
